import React from 'react';
import { X, Mail, Phone, MapPin, User, Briefcase, MessageCircle, Calendar, Users } from 'lucide-react';
import CustomerAvatar from '../../../../components/common/CustomerAvatar';

const CustomerViewModal = ({ customer, onClose, onEdit }) => {
    if (!customer) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <CustomerAvatar name={customer.name} image={customer.image} size="md" />
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Customer Details</h2>
                            <p className="text-xs text-gray-500">
                                {customer.customerId ? `ID: ${customer.customerId}` : 'View customer information and contact details'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <div className="grid grid-cols-12 gap-6">
                        {/* Left Column */}
                        <div className="col-span-12 lg:col-span-7 space-y-5">
                            {/* Basic Information */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                    Basic Information
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Customer Name
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 font-medium">
                                            {customer.name}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Category
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm">
                                            {customer.categoryName ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                                                    {customer.categoryName}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">Not set</span>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Customer ID
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 font-medium">
                                            {customer.customerId || '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            TRN
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 font-medium">
                                            {customer.trn || '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Details */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                                    Contact Details
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Contact Person
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900">
                                            {customer.contactPerson || '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Designation
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900">
                                            {customer.designation || '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Mobile Number
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900">
                                            {customer.mobileNumber || '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            WhatsApp
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900">
                                            {customer.whatsappNumber || '-'}
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Email Address
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900">
                                            {customer.email || '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="col-span-12 lg:col-span-5 space-y-5">
                            {/* Location */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                    Location
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                            Address Line
                                        </label>
                                        <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900">
                                            {customer.address || '-'}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                                Country
                                            </label>
                                            <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900">
                                                {customer.countryName || '-'}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                                                State/Emirate
                                            </label>
                                            <div className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900">
                                                {customer.stateName || '-'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Assigned Sales Team */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                                    Assigned Sales Team
                                </h3>
                                <div className="space-y-2">
                                    {customer.assignedSalesPersons && customer.assignedSalesPersons.length > 0 ? (
                                        customer.assignedSalesPersons.map((sp, index) => (
                                            <div key={index} className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-gray-200">
                                                <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-xs font-bold">
                                                    {(sp.fullName || sp.name).charAt(0)}
                                                </div>
                                                <span className="text-sm font-medium text-gray-700">{sp.fullName || sp.name}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-xs text-gray-500 italic text-center py-2">No assigned sales persons</p>
                                    )}
                                </div>
                            </div>

                            {/* Delegated Sales Team */}
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                                    Delegated Sales Team
                                </h3>
                                <div className="space-y-2">
                                    {customer.delegatedSalesPersons && customer.delegatedSalesPersons.length > 0 ? (
                                        customer.delegatedSalesPersons.map((sp, index) => (
                                            <div key={index} className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-gray-200">
                                                <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs font-bold">
                                                    {(sp.fullName || sp.name).charAt(0)}
                                                </div>
                                                <span className="text-sm font-medium text-gray-700">{sp.fullName || sp.name}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-xs text-gray-500 italic text-center py-2">No delegated sales persons</p>
                                    )}
                                </div>
                            </div>

                            {/* Joined Date */}
                            {customer.createdAt && (
                                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                                    <div className="flex items-center gap-2 text-xs text-blue-900">
                                        <Calendar size={14} className="text-blue-600" />
                                        <div>
                                            <p className="font-semibold">Joined</p>
                                            <p className="text-blue-700">{formatDate(customer.createdAt)}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm"
                    >
                        Close
                    </button>
                    <button
                        onClick={() => {
                            onEdit(customer);
                            onClose();
                        }}
                        className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all font-semibold text-sm flex items-center gap-2"
                    >
                        Edit Customer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomerViewModal;
